import { reactive } from "vue";
import axiosInstance from '@/axiosInstance';
import questionsData from "@/assets/data/questions_Test.json";
import router from "@/router"; // Import router if using Vue Router

class SurveyService {
  state = reactive({
    stages: [],
    answers: {},
    allSurveyQuestions: [], // New array to hold all questions in order
  });

  constructor() {
    this.loadQuestions();
  }

  loadQuestions() {
    this.state.stages = questionsData.stages.map((stage) => ({
      id: stage.stage,
      name: `Stage ${stage.stage}`,
      completed: false,
      questions: stage.questions.map(q => ({ ...q, required: q.required || false })), // Ensure required is always a boolean
      page_header: stage.page_header,
      page_images: stage.page_images,
      page_options: stage.page_options,
    }));
    // Populate allSurveyQuestions by flattening questions from all stages
    this.state.allSurveyQuestions = this.state.stages.flatMap(stage =>
      stage.questions.map(question => ({ ...question, stageId: stage.id }))
    );
  }

  getStage(stageNumber) {
    return this.state.stages.find((stage) => stage.id === stageNumber);
  }

  getStages() {
    return this.state.stages;
  }

  getImageByStage(stageId, pageNumber) {
    const img = questionsData['speech-bubble']?.[`stage-${stageId}`]?.[`page-${pageNumber}`]?.img;
    console.log(img);
    return img || null; // Return the image or null if not found
  }

  canAccessStage(stageId) {
    const currentStageIndex = this.state.stages.findIndex((stage) => stage.id === stageId);
    return currentStageIndex === 0 || this.state.stages[currentStageIndex - 1].completed;
  }

  completeStage(stageId) {
    const stage = this.state.stages.find((stage) => stage.id === stageId);
    if (stage) stage.completed = true;
  }

  setAnswer(questionId, answer) {
    // Debug logging for Q3A1NR and Q3A1 to track city values
    if (questionId === '3a1nr' || questionId === '3a1' || questionId === '3a') {
      console.log(`🏙️ CITY/COUNTRY VALUE SET - ${questionId}:`, {
        value: answer,
        type: typeof answer,
        length: answer?.length,
        isNumber: !isNaN(answer),
        isObject: typeof answer === 'object',
        keys: typeof answer === 'object' ? Object.keys(answer) : null
      });
    }
    
    // UNIVERSAL LOGGING: Log ALL field setting attempts
    console.log(`📝 SET ANSWER: ${questionId} = ${JSON.stringify(answer)}`);
    
    // CRITICAL: Prevent Q3A2/Q3A2R from being set for Non-Residents
    if (questionId === '3a2' || questionId === '3a2r' || questionId === 'Q3A2R' || 
        questionId === 'q3a2r' || questionId.toLowerCase().includes('3a2')) {
      const currentQ3A = this.state.answers['3a'];
      const currentQ3A4 = this.state.answers['3a4'];
      
      // Check both Q3A and Q3A4 to determine residency
      let isUSResident = false;
      
      if (currentQ3A4) {
        isUSResident = (currentQ3A4 === '999');
      } else if (currentQ3A) {
        // Fallback: check Q3A directly — exact match only
        let countryCode = currentQ3A;
        if (typeof countryCode === 'object' && countryCode.value) {
          countryCode = countryCode.value;
        } else if (typeof countryCode === 'object' && countryCode.key) {
          countryCode = countryCode.key;
        }
        countryCode = String(countryCode);
        isUSResident = (countryCode === '99900');
      }
      
      if (!isUSResident) {
        console.log(`🚫 BLOCKED: Preventing ${questionId} from being set for Non-Resident (Q3A: ${currentQ3A}, Q3A4: ${currentQ3A4})`);
        return; // Don't set the answer
      }
      console.log(`✅ ALLOWED: Setting ${questionId} for US Resident (Q3A4: ${currentQ3A4})`);
    }
    
    this.state.answers[questionId] = answer;
    
    // CRITICAL: Clean up Q3A2/Q3A2R immediately when Q3A changes
    if (questionId === '3a') {
      console.log('🔄 Q3A changed, cleaning up dependent fields immediately');
      
      // Determine residency status
      let countryCode = answer;
      if (typeof countryCode === 'object' && countryCode.value) {
        countryCode = countryCode.value;
      } else if (typeof countryCode === 'object' && countryCode.key) {
        countryCode = countryCode.key;
      }
      
      countryCode = String(countryCode);
      // Exact match only — only "99900" is US resident
      const isUSResident = (countryCode === '99900');
      
      console.log('🔄 Q3A immediate cleanup:', {
        countryCode,
        isUSResident
      });
      
      if (!isUSResident) {
        // Non-Resident: Immediately clear US resident fields
        console.log('🧹 IMMEDIATE: Clearing US resident fields for Non-Resident');
        delete this.state.answers['3a2'];   // Q3A2R state field
        delete this.state.answers['3a2r'];  // Alternative name
        delete this.state.answers['3a3'];   // ZIP field
        delete this.state.answers['3a1'];   // US city field
        
        // Set Q3A4 to the full country code (not a 3-digit prefix)
        this.state.answers['3a4'] = countryCode;
        console.log('🔄 Set Q3A4 for Non-Resident:', countryCode);
      } else {
        // US Resident: Clear Non-Resident fields
        console.log('🧹 IMMEDIATE: Clearing Non-Resident fields for US Resident');
        delete this.state.answers['3a1nr']; // Non-Resident city field
        
        // Set Q3A4 to '999' for US resident
        this.state.answers['3a4'] = '999';
        console.log('🔄 Set Q3A4 for US Resident: 999');
      }
    }
    
    console.log('Set answer triggered:', this.state.answers);
  }

  getAnswer(questionId) {
    return this.state.answers[questionId];
  }

  getAnswers() {
    return this.state.answers;
  }

  setAnswers(newAnswers) {
    console.log("calling ...");
    this.state.answers = { ...this.state.answers, ...newAnswers };
  }

  getQuestionsByStage(stageId) {
    const stage = this.state.stages.find((stage) => stage.id === stageId);
    return stage ? stage.questions : [];
  }

  isAllMandatoryQuestionsAnswered(stageId) {
    const stage = this.state.stages.find((stage) => stage.id === stageId);
    if (!stage) return false;

    return stage.questions.every((question) => {
      if (question.required) {
        const answer = this.state.answers[question.id];
        return answer !== undefined && answer !== null && answer !== "";
      }
      return true;
    });
  }

  getPageHeader(stageNumber, pageNumber) {
    const stage = this.state.stages.find((s) => s.id === stageNumber);
    return stage && stage.page_header ? stage.page_header[pageNumber] : null;
  }

  reset() {
    console.log('🔄 Resetting survey - clearing all answers');
    this.state.stages.forEach((stage) => (stage.completed = false));
    this.state.answers = {};
    
    // Clear localStorage items related to the survey
    localStorage.removeItem('surveyUniqueId');
    localStorage.removeItem('surveySubmitted');
    localStorage.removeItem('submissionTime');
    localStorage.removeItem('surveyTerminationReason');
    localStorage.removeItem('surveyTerminationMessage');
    
    console.log('✅ Survey reset complete');
  }


  isPageBrekable() {
    const answers = this.getAnswers();

    // Termination based on 7a - ONLY for Government/Military
    const q7aAnswer = answers?.["7a"];
    
    // Check for Government/Military selection (value "8")
    // The answer can be stored as string "8", object with key "8", or object with key "Government/Military"
    let isGovtOrMilitary = false;
    if (q7aAnswer) {
      if (typeof q7aAnswer === 'string') {
        isGovtOrMilitary = q7aAnswer === "8";
      } else if (typeof q7aAnswer === 'object') {
        isGovtOrMilitary = q7aAnswer.key === "8" || q7aAnswer.key === "Government/Military" || q7aAnswer.value === "8";
      }
    }

    // Termination based on question 6
    const q6 = answers?.["6"];
    const exceedsLimit = (value, type) =>
      (type === "days" && value > 730) || (type === "month" && value > 24);

    const a = q6?.a;
    const b = q6?.b;

    const isExceeds = (a && exceedsLimit(a.value, a.type)) || (b && exceedsLimit(b.value, b.type));
    
    // Log for debugging
    if (isGovtOrMilitary) {
      console.log('🛑 Survey termination triggered by Q7a Government/Military selection');
    }
    
    // Return true if either termination condition is met
    return isGovtOrMilitary || isExceeds;
  }

  // New method specifically for Q7a Government/Military termination
  terminateSurveyForGovernmentMilitary() {
    console.log('🛑 Terminating survey for Government/Military selection');
    
    // Set a flag to indicate this was a Government/Military termination
    localStorage.setItem('surveyTerminationReason', 'government-military');
    localStorage.setItem('surveyTerminationMessage', 'Thank you for your time. This survey is not applicable for government/military travel.');
    
    // Mark survey as completed without submitting to backend
    this.setSurveyCompleted();
    
    // Import router here to avoid circular dependency
    import('@/router').then(({ default: router }) => {
      router.push({ name: "SurveyEnd" });
    });
  }

  isSurveySubmitted() {
    const storedSubmissionTime = localStorage.getItem("submissionTime");

    if (!storedSubmissionTime) {
      return { restrictSubmission: false, remainingTime: 0 };
    }

    const submissionTime = new Date(storedSubmissionTime).getTime();
    const currentTime = new Date().getTime();
    const timeDifference = currentTime - submissionTime;
    const restrictSubmission = timeDifference < 5 * 60 * 1000;
    const remainingTime = Math.max(0, 5 * 60 * 1000 - timeDifference); // Calculate remaining time in milliseconds

    console.log(`Remaining time before next submission: ${Math.ceil(remainingTime / 1000)} seconds`);

    return { restrictSubmission, remainingTime };
  }

  setSurveyCompleted() {
    localStorage.setItem("surveySubmitted", "true");
    localStorage.setItem("submissionTime", new Date().toISOString());
  }

  transformAnswerKeys(answers) {
    console.log('=== TRANSFORM ANSWER KEYS START ===');
    console.log('Raw answers received:', JSON.stringify(answers, null, 2));
    
    // Handle Q3A4 and Q3A1NR logic FIRST
    // Q3A4 determines residency status: 999 = US Resident, other = Non-Resident
    if (answers['3a']) {
      let countryCode = answers['3a'];
      
      console.log('Q3A raw value:', countryCode, 'Type:', typeof countryCode);
      
      // Extract country code from object or string
      if (typeof countryCode === 'object' && countryCode.value) {
        countryCode = countryCode.value;
      } else if (typeof countryCode === 'object' && countryCode.key) {
        countryCode = countryCode.key;
      }
      
      countryCode = String(countryCode);
      // Exact match only — only "99900" is US resident
      const isUSResident = (countryCode === '99900');
      
      console.log('Q3A4 calculation:', {
        originalCountryCode: answers['3a'],
        extractedCountryCode: countryCode,
        isUSResident,
        willSetQ3A4To: isUSResident ? '999' : countryCode
      });
      
      if (isUSResident) {
        // US Resident
        answers['3a4'] = '999';
        
        // BUG 1 FIX: Extract plain numeric value from Q3A2 multiselect object
        // dropdown-search stores {value: "17", name: "Illinois (IL)"} — backend needs just "17"
        if (answers['3a2'] && typeof answers['3a2'] === 'object') {
          answers['3a2'] = answers['3a2'].value || answers['3a2'].key || answers['3a2'];
          console.log('Q3A2 extracted value:', answers['3a2']);
        }
        
        console.log('US Resident Q3A BEFORE transformation:', {
          '3a1': answers['3a1'],
          '3a1nr': answers['3a1nr'],
          '3a2': answers['3a2'],
          '3a3': answers['3a3']
        });
        
        // Move 3a1nr to 3a1 if needed (shouldn't happen, but safety check)
        if (answers['3a1nr'] && !answers['3a1']) {
          console.log('WARNING: Moving 3a1nr to 3a1:', answers['3a1nr']);
          answers['3a1'] = answers['3a1nr'];
        }
        
        // CRITICAL: Delete non-resident field
        // 3a1nr should not exist for US residents
        delete answers['3a1nr'];
        
        console.log('US Resident Q3A AFTER transformation:', {
          '3a1': answers['3a1'],
          '3a1nr': answers['3a1nr'],
          '3a2': answers['3a2'],
          '3a3': answers['3a3']
        });
        
      } else {
        // Non-Resident
        answers['3a4'] = countryCode;
        
        console.log('Non-Resident Q3A BEFORE transformation:', {
          '3a1': answers['3a1'],
          '3a1nr': answers['3a1nr'],
          '3a2': answers['3a2'],
          '3a2r': answers['3a2r'],
          '3a3': answers['3a3']
        });
        
        // Do NOT move 3a1 to 3a1nr — 3a1 is US resident city, 3a1nr is NR city.
        // Moving 3a1 to 3a1nr would corrupt NR data with stale US resident city values.
        
        // CRITICAL: Delete ALL US resident fields for non-residents
        // These should not exist for non-residents at all
        delete answers['3a1'];   // US city
        delete answers['3a2'];   // US state (Q3A2R) - MUST be blank for NR
        delete answers['3a2r'];  // Alternative state field name
        delete answers['3a3'];   // US ZIP
        
        // Ensure they are truly removed by setting to undefined
        answers['3a1'] = undefined;
        answers['3a2'] = undefined;
        answers['3a2r'] = undefined;
        answers['3a3'] = undefined;
        
        // EXTRA SAFETY: Force remove any Q3A2R variations
        delete answers['Q3A2R'];
        delete answers['q3a2r'];
        answers['Q3A2R'] = undefined;
        answers['q3a2r'] = undefined;
        
        console.log('Non-Resident Q3A AFTER transformation:', {
          '3a1': answers['3a1'],
          '3a1nr': answers['3a1nr'],
          '3a2': answers['3a2'],
          '3a2r': answers['3a2r'],
          '3a3': answers['3a3']
        });
      }
      
      console.log('Q3A processing:', {
        isUSResident,
        '3a4': answers['3a4'],
        '3a1': answers['3a1'],
        '3a1nr': answers['3a1nr'],
        '3a2': answers['3a2'],
        '3a2r': answers['3a2r']
      });
    }
    
    // FIX: Q12D country — same issue as Q13: FieldGroup stores under field_name ("Country of currency")
    // and multiselect stores full object {value, name} — extract raw value and remap to key_name "country"
    if (answers['12.d'] && typeof answers['12.d'] === 'object') {
      let raw12d = answers['12.d']['Country of currency'] ?? answers['12.d'].country;
      if (raw12d !== undefined) {
        if (typeof raw12d === 'object' && raw12d !== null) {
          raw12d = raw12d.value || raw12d.key || '';
        }
        raw12d = String(raw12d).trim();
        if (/^\d{5}$/.test(raw12d)) {
          answers['12.d'].country = raw12d;
          console.log(`✅ Q12D country set: "${raw12d}"`);
        } else {
          console.warn(`⚠️ Q12D country invalid "${raw12d}" — clearing`);
          delete answers['12.d'].country;
        }
        delete answers['12.d']['Country of currency'];
      }
      console.log('Q12.d data structure:', JSON.stringify(answers['12.d'], null, 2));
    }
    
    // Transform Q11 data - MUST happen AFTER Q3A4 is set
    if (answers['11'] && Array.isArray(answers['11'])) {
      const isUSResident = answers['3a4'] === '999';
      
      console.log('Q11 transformation starting:', {
        isUSResident,
        destinationCount: answers['11'].length
      });
      
      answers['11'] = answers['11'].map((destination, index) => {
        const transformed = { ...destination };
        
        console.log(`Q11 destination ${index} BEFORE:`, JSON.stringify(transformed, null, 2));
        
        // BUG 2 FIX: Extract plain values from multiselect objects {value, name}
        // dropdown-search stores objects — we need the raw numeric string
        let stateValue = '';
        if (transformed.state) {
          if (typeof transformed.state === 'object') {
            stateValue = String(transformed.state.value || transformed.state.key || '').trim();
          } else if (typeof transformed.state === 'string') {
            stateValue = transformed.state.trim();
          }
        }
        
        let countryValue = '';
        if (transformed.country) {
          if (typeof transformed.country === 'object') {
            countryValue = String(transformed.country.value || transformed.country.key || '').trim();
          } else if (typeof transformed.country === 'string') {
            countryValue = transformed.country.trim();
          }
        }
        
        // Determine if this is a US state or international country
        // US state codes are 1-78 (numeric)
        // International country codes are 5 digits (>= 10000)
        // If stateValue >= 10000, it's a country code stored in the wrong field — move it to country
        const stateValueNum = stateValue ? parseInt(stateValue.toString()) : NaN;
        const countryValueNum = countryValue ? parseInt(countryValue.toString()) : NaN;
        
        const isUSState = !isNaN(stateValueNum) && stateValueNum >= 1 && stateValueNum <= 78;
        // A 5-digit number in either field is a country code
        const stateIsCountryCode = !isNaN(stateValueNum) && stateValueNum >= 10000;
        const isInternationalCountry = (!isNaN(countryValueNum) && countryValueNum >= 10000) || stateIsCountryCode;
        // 501, 999 alone (not 5-digit) are invalid
        const isInvalidState = stateValue && !isUSState && !stateIsCountryCode;
        const isInvalidCountry = countryValue && !isNaN(countryValueNum) && countryValueNum < 10000;
        
        console.log(`Q11 destination ${index} analysis:`, {
          stateValue, countryValue, stateValueNum, countryValueNum,
          isUSState, stateIsCountryCode, isInternationalCountry, isInvalidState, isInvalidCountry
        });
        
        if (isUSState) {
          // Valid US state — clear country
          transformed.state = stateValue;
          delete transformed.country;
          console.log(`Q11 destination ${index}: US State detected, clearing country`);
        } else if (stateIsCountryCode) {
          // Country code was stored in state field — move it to country
          transformed.country = stateValue;
          delete transformed.state;
          console.log(`Q11 destination ${index}: Country code in state field (${stateValue}), moved to country`);
        } else if (!isNaN(countryValueNum) && countryValueNum >= 10000) {
          // Valid country code in country field
          transformed.country = countryValue;
          delete transformed.state;
          console.log(`Q11 destination ${index}: International country detected, clearing state`);
        } else {
          // No valid code — clear both
          delete transformed.state;
          delete transformed.country;
          console.log(`Q11 destination ${index}: No valid state or country code, clearing both`);
        }
        
        console.log(`Q11 destination ${index} AFTER:`, JSON.stringify(transformed, null, 2));
        
        return transformed;
      });
      
      console.log('Q11 transformed final:', JSON.stringify(answers['11'], null, 2));
    }

    console.log('Q3A4 and Q3A1NR processed:', {
      '3a4': answers['3a4'],
      '3a1': answers['3a1'],
      '3a1nr': answers['3a1nr'],
      '3a2': answers['3a2'],
      '3a2r': answers['3a2r']
    });

    // FINAL CHECK: Ensure Q3A1NR is preserved for Non-Residents
    if (answers['3a4'] !== '999' && !answers['3a1nr']) {
      console.warn('⚠️ WARNING: Non-Resident detected but Q3A1NR is empty!', {
        '3a4': answers['3a4'],
        '3a1nr': answers['3a1nr'],
        allAnswers: Object.keys(answers)
      });
    }

    // FINAL CHECK: Log complete answers being sent to backend
    console.log('=== FINAL ANSWERS BEING SENT TO BACKEND ===');
    console.log(JSON.stringify(answers, null, 2));
    console.log('=== Q3A1NR VALUE ===', answers['3a1nr']);
    console.log('=== Q3A1 VALUE ===', answers['3a1']);
    console.log('=== Q3A2 VALUE ===', answers['3a2']);
    console.log('=== Q3A2R VALUE ===', answers['3a2r']);

    // FINAL CLEANUP: Remove ANY field containing "3a2" for Non-Residents
    if (answers['3a4'] && answers['3a4'] !== '999') {
      console.log('🧹 FINAL CLEANUP: Removing all Q3A2-related fields for Non-Resident');
      Object.keys(answers).forEach(key => {
        if (key.toLowerCase().includes('3a2')) {
          console.log(`🗑️ FINAL: Deleting ${key} = ${answers[key]}`);
          delete answers[key];
        }
      });
    }

    // FIX: Q10 total nights — always recompute from sub-fields, never trust stored value
    if (answers['10'] && typeof answers['10'] === 'object') {
      const usaNights = Number(answers['10'].usaNights) || 0;
      const outsideNights = Number(answers['10'].outsideNights) || 0;
      answers['10'].totalNights = usaNights + outsideNights;
      console.log('Q10 total nights recomputed:', answers['10']);
    }

    // FIX: Q3A4 validation — must be '999' (US) or a valid 5-digit country code
    if (answers['3a4'] !== undefined) {
      const q3a4 = String(answers['3a4']);
      const isValidUS = q3a4 === '999';
      const isValidNonUS = /^\d{5}$/.test(q3a4);
      if (!isValidUS && !isValidNonUS) {
        console.warn(`⚠️ Q3A4 invalid value "${q3a4}" — attempting to recover from Q3A`);
        // Attempt recovery from the raw Q3A country answer
        if (answers['3a']) {
          let raw = answers['3a'];
          if (typeof raw === 'object') raw = raw.value || raw.key || '';
          raw = String(raw);
          if (raw === '99900') {
            answers['3a4'] = '999';
          } else if (/^\d{5}$/.test(raw)) {
            answers['3a4'] = raw;
          } else {
            console.error(`❌ Q3A4 could not be recovered — raw Q3A value: "${raw}"`);
          }
        }
      }
      console.log('Q3A4 validated:', answers['3a4']);
    }

    // FIX: Q13 country — FieldGroup stores under field_name ("Country of currency") not key_name ("country")
    // Also multiselect stores full object {value, name} — extract raw value string
    if (answers['13'] && typeof answers['13'] === 'object') {
      // FieldGroup.vue uses field_name as key, so look for "Country of currency" first
      let raw13 = answers['13']['Country of currency'] ?? answers['13'].country;
      if (raw13 !== undefined) {
        if (typeof raw13 === 'object' && raw13 !== null) {
          raw13 = raw13.value || raw13.key || '';
        }
        raw13 = String(raw13).trim();
        if (/^\d{5}$/.test(raw13)) {
          answers['13'].country = raw13;
          console.log(`✅ Q13 country set: "${raw13}"`);
        } else {
          console.warn(`⚠️ Q13 country invalid "${raw13}" — clearing`);
          delete answers['13'].country;
        }
        // Remove the field_name-keyed duplicate regardless
        delete answers['13']['Country of currency'];
      }
    }

    // FIX: Q23B (country of currency) — must be a valid 5-digit country code
    // Default to Q3A4-based country: US resident -> 99900, non-US -> Q3A4 value
    if (answers['23b'] !== undefined) {
      let q23b = answers['23b'];
      if (typeof q23b === 'object') q23b = q23b.value || q23b.key || '';
      q23b = String(q23b);
      if (!/^\d{5}$/.test(q23b)) {
        // Invalid — derive from Q3A4
        const q3a4 = answers['3a4'];
        if (q3a4 === '999') {
          answers['23b'] = '99900'; // US resident -> United States
        } else if (q3a4 && /^\d{5}$/.test(String(q3a4))) {
          answers['23b'] = String(q3a4); // Non-US -> their country code
        } else {
          delete answers['23b'];
        }
        console.log(`Q23B invalid "${q23b}" — corrected to: ${answers['23b']}`);
      }
    }

    // Clean up undefined values from the answers object
    Object.keys(answers).forEach(key => {
      if (answers[key] === undefined) {
        delete answers[key];
      }
    });

    // Return answers unchanged - backend expects lowercase
    return answers;
  }

  submitSurvey() {
    const qrcode = localStorage.getItem("qrcode");

    if (!qrcode) {
      alert("QR code is missing. Please scan again.");
      return;
    }

    const answers = this.getAnswers();

    if (!answers || Object.keys(answers).length === 0) {
      alert("Please answer all questions before submitting.");
      return;
    }

    // Transform answer keys to match backend format
    const transformedAnswers = this.transformAnswerKeys(answers);

    const payload = {
      qr_id: qrcode,
      question_id: 1,
      answer: transformedAnswers,
      unique_id: this.getUniqueId(),
    };

    console.log("=== SUBMITTING SURVEY PAYLOAD ===");
    console.log(JSON.stringify(payload, null, 2));
    console.log("=== PAYLOAD Q3A1NR ===", payload.answer['3a1nr']);
    console.log("=== PAYLOAD Q3A1 ===", payload.answer['3a1']);

    router.push({ name: "SurveyEndProgress" });

    axiosInstance
      .post("public/answers", payload)
      .then(() => {
        this.setSurveyCompleted(); // Call the method with "this"
        this.removedUniqueId(); // Clear the unique ID after submission
        router.push({ name: "SurveyEnd" }); // Ensure router is imported
      })
      .catch((error) => {
        console.error("Error submitting survey:", error);
        const errorMessage = error.response?.data?.message || "Failed to submit survey. Please try again.";
        alert(errorMessage);
      });
  }

  // Modified to use allSurveyQuestions for total count
  getTotalQuestions() {
    return this.state.allSurveyQuestions.length;
  }

  getAnsweredQuestionsCount() {
    return Object.keys(this.state.answers).length;
  }

  calculateOverallCompletion() {
    const totalQuestions = this.getTotalQuestions();
    const answeredQuestions = this.getAnsweredQuestionsCount();
    return totalQuestions > 0 ? Math.round((answeredQuestions / totalQuestions) * 100) : 0;
  }

  calculatePageCompletion(stageId, pageNumber) {
    const stage = this.getStage(stageId);
    if (!stage || !stage.questions) {
      return 0;
    }

    const pageQuestions = stage.questions.filter(q => q.page === pageNumber);
    const totalQuestionsOnPage = pageQuestions.length;
    const answeredQuestionsOnPage = pageQuestions.filter(q => this.state.answers[q.id] !== undefined && this.state.answers[q.id] !== null && this.state.answers[q.id] !== "").length;

    return totalQuestionsOnPage > 0 ? Math.round((answeredQuestionsOnPage / totalQuestionsOnPage) * 100) : 0;
  }

  calculateOverallPageCompletion(stageId) {
    const stage = this.getStage(stageId);
    if (!stage || !stage.questions) {
      return {};
    }

    const questionsByPage = {};
    stage.questions.forEach(q => {
      if (!questionsByPage[q.page]) {
        questionsByPage[q.page] = [];
      }
      questionsByPage[q.page].push(q);
    });

    const overallPageCompletion = {};
    for (const pageNumber in questionsByPage) {
      const pageQuestions = questionsByPage[pageNumber];
      const totalQuestionsOnPage = pageQuestions.length;
      const answeredQuestionsOnPage = pageQuestions.filter(q => this.state.answers[q.id] !== undefined && this.state.answers[q.id] !== null && this.state.answers[q.id] !== "").length;
      overallPageCompletion[pageNumber] = totalQuestionsOnPage > 0 ? Math.round((answeredQuestionsOnPage / totalQuestionsOnPage) * 100) : 0;
    }

    return overallPageCompletion;
  }

  // New method to get the index of a question by its ID from the flattened list
  getQuestionIndex(questionId) {
    return this.state.allSurveyQuestions.findIndex(question => question.id === questionId);
  }

  // New method to calculate progress based on the current question's position
  calculateProgressByCurrentQuestionId(currentQuestionId) {
    const totalQuestions = this.getTotalQuestions();
    if (totalQuestions === 0 || !currentQuestionId) {
      return 0;
    }

    const currentQuestionIndex = this.getQuestionIndex(currentQuestionId);
    if (currentQuestionIndex === -1) {
      // If the question ID is not found, return 0 or handle as an error
      console.warn(`Question ID '${currentQuestionId}' not found in allSurveyQuestions.`);
      return 0;
    }

    // +1 because index is 0-based, and we want 1-based count for percentage
    return Math.round(((currentQuestionIndex + 1) / totalQuestions) * 100);
  }



  // Unique handler for question 6 to manage its sub-questions

  generateUniqueId() {
    const uniqueId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0,
        v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
    localStorage.setItem('surveyUniqueId', uniqueId);
    return uniqueId;
  }

  removedUniqueId() {
    localStorage.removeItem('surveyUniqueId');
  }

  getUniqueId() {
    // Get existing unique ID or generate a new one
    let uniqueId = localStorage.getItem('surveyUniqueId');

    if (!uniqueId) {
      // Generate new unique ID if it doesn't exist
      uniqueId = this.generateUniqueId();
    }

    return uniqueId;
  }

  saveProgress() {
    const qrcode = localStorage.getItem("qrcode");
    const uniqueId = this.getUniqueId();

    if (!qrcode || !uniqueId) {
      console.warn("Cannot save progress: QR code or unique ID is missing.");
      return;
    }


    const answers = this.getAnswers();

    if (!answers || Object.keys(answers).length === 0) {
      console.warn("No answers to save.");
      return;
    }

    // Transform answer keys to match backend format
    const transformedAnswers = this.transformAnswerKeys(answers);

    axiosInstance
      .post("public/answersProgress", {
        qr_id: qrcode,
        unique_id: uniqueId,
        question_id: 1, // Ensure this ID is dynamic if needed
        answer: transformedAnswers, // Use transformed answers
      })
      .then(() => {
        console.log("Progress saved successfully.");
      })
      .catch((error) => {
        console.error("Error submitting survey:", error);
      });
  }



}

export default new SurveyService();